import { ADMIN_PAGES } from "../shared/adminPages";
import { DOCUMENT_MODULES } from "../shared/documentModules";
/** URL scope is a route contract, independent of the current user's permissions.
 * Authorization always happens during admission and again at the API boundary.
 * Mixed namespaces (admin/integrations) deliberately declare individual families.
 */
export type RouteScope = "project" | "global" | "public" | "transport";
interface RouteContract {
    path: string;
    scope: RouteScope;
    switchTo?: string;
    /** A global page that runs without a project: a staff registry session (no project)
     *  opens it with a project-free query client instead of being asked for a project, and
     *  the shell draws no project areas on it (`useProjectChrome`). */
    projectFree?: true;
}
const project = (path: string, switchTo = path): RouteContract => ({
    path,
    scope: "project",
    switchTo,
});
const global = (path: string): RouteContract => ({ path, scope: "global" });
/**
 * Every admin page's contract (`ADMIN_PAGES`), and a project route for each moved page's old
 * address, which redirects. A longer path comes before a page it sits under, since the first
 * match wins (`/admin/flags/global` is staff's, `/admin/flags` the tenant's).
 */
const ADMIN_CONTRACTS: readonly RouteContract[] = [
    ...ADMIN_PAGES.flatMap((page): RouteContract[] =>
        page.scope === "project"
            ? [project(page.path, page.switchTo)]
            : page.scope === "global"
              ? [
                    {
                        path: page.path,
                        scope: "global",
                        ...(page.projectFree && { projectFree: true }),
                    },
                ]
              : [],
    ),
    ...ADMIN_PAGES.flatMap((page) => (page.movedFrom ? [project(page.movedFrom, page.path)] : [])),
].sort((a, b) => b.path.length - a.path.length);

export const ROUTE_CONTRACTS: readonly RouteContract[] = [
    ...ADMIN_CONTRACTS,
    { path: "/guest", scope: "public" },
    // A product update email's stop page (signed out, keyed by the copy's token).
    { path: "/email/stop", scope: "public" },
    ...["/api", "/static", "/embedpdf", "/src", "/manifest.json", "/favicon.ico"].map(
        (path): RouteContract => ({ path, scope: "transport" }),
    ),
    ...[
        "/authenticate",
        "/embed-auth",
        "/embed-test",
        "/embed",
        "/logout",
        "/projects",
        "/offline",
        "/favorites",
        "/this-machine",
        // The Connected Apps redirect is registered bare. A URL-catalog session cannot
        // use the canonical consent endpoint, so the page is never moved under a prefix.
        "/oauth/authorize",
        // The retired Engagement reports' address: it redirects to Usage, which runs without a project.
        "/admin/engagement",
    ].map(global),
    // The portfolio names the tenants it reads, but the header and Marvin act on the tab's project.
    project("/portfolio"),
    ...[
        "cr",
        "control-tower",
        "workflow-studio",
        "rn",
        "fc",
        "quality",
        "rfi",
        "search",
        "explore",
        "workbench",
        "shop-review",
        "clash-review",
        "rc",
        "submittal",
        "submittal-register",
        "submittal-registry",
        "upload",
    ].map((name) => project(`/w/${name}`)),
    project("/w/tools", "/"),
    project("/rfi-links", "/rfi-links/review"),
    project("/sheets/revisions", "/admin/revisions"),
    project("/doc/specs", "/doc/specs"),
    project("/doc", "/doc/sheets"),
    project("/documents"),
    project("/c"),
    project("/table-edit", "/documents"),
    project("/integrations/data-status"),
    project("/integrations"),
    project("/settings/project"),
    project("/settings/workflows"),
    project("/settings"),
    project("/apps"),
    ...[
        "/drawings",
        "/specifications",
        "/specs",
        "/sheets",
        "/team",
        "/directory",
        "/areas",
        "/rfis",
        "/submittals",
        "/schedule",
        "/timeline",
        "/debug",
        "/connect",
    ].map((path) => project(path)),
    // Only the index routes: these must not silently classify a new admin/tool route. `/admin`
    // (the admin Overview) comes from ADMIN_CONTRACTS.
    project("/"),
];

export function routeContract(path: string): RouteContract | null {
    const pathname = path.split(/[?#]/, 1)[0].replace(/\/$/, "") || "/";
    const lower = pathname.toLowerCase();
    return (
        ROUTE_CONTRACTS.find(({ path: prefix }) => {
            const root = prefix.toLowerCase();
            return (
                lower === root ||
                (root !== "/" && root !== "/admin" && lower.startsWith(`${root}/`))
            );
        }) ?? null
    );
}

/** Switches retain a page family, never an entity/filter from another project. */
export function projectSwitchPath(path: string): string {
    const pathname = path.split(/[?#]/, 1)[0];
    // A project's page names its project; a switch keeps the family, never the other project.
    if (/^\/admin\/revisionDash(?:\/|$)/i.test(pathname)) return "/admin/revisionDash";
    const contract = routeContract(pathname);
    if (contract?.path === "/documents") {
        const categoryPath = pathname.split("/").slice(0, 3).join("/");
        // Use the module catalogue, never carry a legacy document UUID as a category.
        return Object.values(DOCUMENT_MODULES).some((module) => module.route === categoryPath)
            ? categoryPath
            : "/documents";
    }
    return contract?.switchTo ?? "/";
}
